import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { api } from "@/api/client";

export interface Branding {
    company: { id: number | false; name: string };
    colors: Record<string, string>;
    font_family: string;
    font_size: string;
    page_size: number;
    logo_url: string | false;
    background_url: string | false;
    user: {
        theme: string;
        density: string;
        sidebar_mode: string;
    } | null;
}

export const THEME_IDS = ["light", "glass", "colorful"];
export const DENSITY_IDS = ["comfortable", "compact"];
export const SIDEBAR_MODE_IDS = ["full", "icon"];

export const FONT_SIZES: Record<string, string> = {
    default: "14px",
    medium: "16px",
    large: "18px",
    huge: "22px",
};

const THEME_COOKIE = "mk_theme";

function normalizeHex(value: string | false | null | undefined): string {
    if (!value) {
        return "";
    }
    let hex = String(value).trim().replace(/^#/, "");
    if (hex.length === 3) {
        hex = hex.split("").map((part) => part + part).join("");
    }
    return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex.toLowerCase()}` : "";
}

function hexToRgb(value: string | false | null | undefined): [number, number, number] | null {
    const hex = normalizeHex(value);
    if (!hex) {
        return null;
    }
    const number = Number.parseInt(hex.slice(1), 16);
    return [(number >> 16) & 255, (number >> 8) & 255, number & 255];
}

export function rgbString(value: string | false | null | undefined): string {
    const rgb = hexToRgb(value);
    return rgb ? rgb.join(", ") : "";
}

export function readableOn(value: string | false | null | undefined): string {
    const rgb = hexToRgb(value);
    if (!rgb) {
        return "#ffffff";
    }
    const [r, g, b] = rgb.map((part) => {
        const channel = part / 255;
        return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    }) as [number, number, number];
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 0.45 ? "#101424" : "#ffffff";
}

export function readableOnGradient(from: string, to?: string | false): string {
    const start = readableOn(from);
    const end = readableOn(to || from);
    return start === end ? start : "#ffffff";
}

function readCookie(name: string): string | null {
    const match = document.cookie.split("; ").find((part) => part.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function readThemePref(): string | null {
    try {
        return readCookie(THEME_COOKIE) || window.localStorage.getItem(THEME_COOKIE);
    } catch {
        return readCookie(THEME_COOKIE);
    }
}

function writeThemePref(value: string) {
    if (!value) {
        return;
    }
    document.cookie = `${THEME_COOKIE}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
    try {
        window.localStorage.setItem(THEME_COOKIE, value);
    } catch {
        return;
    }
}

function systemTheme(): string {
    try {
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "glass" : "light";
    } catch {
        return "light";
    }
}

function resolveTheme(value: string | null | undefined, fallback: string): string {
    if (value === "system" || !value || !THEME_IDS.includes(value)) {
        return THEME_IDS.includes(fallback) ? fallback : "light";
    }
    return value;
}

export interface ThemeState {
    theme: string;
    density: string;
    sidebarMode: string;
}

function initialState(branding: Branding | null): ThemeState {
    const prefs = branding?.user;
    if (prefs) {
        return {
            theme: resolveTheme(prefs.theme, "light"),
            density: DENSITY_IDS.includes(prefs.density) ? prefs.density : "comfortable",
            sidebarMode: SIDEBAR_MODE_IDS.includes(prefs.sidebar_mode) ? prefs.sidebar_mode : "full",
        };
    }
    const stored = readThemePref();
    return {
        theme: stored && THEME_IDS.includes(stored) ? stored : systemTheme(),
        density: "comfortable",
        sidebarMode: "full",
    };
}

export function rootStyleFor(branding: Branding | null): CSSProperties {
    const style: Record<string, string> = {};
    const colors = branding?.colors ?? {};
    for (const [name, value] of Object.entries(colors)) {
        const rgb = rgbString(value);
        if (rgb) {
            style[`--mk-${name}`] = value;
            style[`--mk-${name}-rgb`] = rgb;
        }
    }
    if (colors.primary) {
        style["--mk-accent"] = colors.primary;
        style["--mk-accent-rgb"] = rgbString(colors.primary);
        style["--mk-text-on-accent"] = readableOnGradient(colors.primary, colors.secondary);
    }
    const font = branding?.font_family || "Inter";
    style["--mk-font"] = `'${font}', 'Cairo', system-ui, sans-serif`;
    style["--mk-font-size"] = FONT_SIZES[branding?.font_size ?? ""] || "14px";
    style["--mk-bg-image"] = branding?.background_url ? `url(${branding.background_url})` : "none";
    return style as CSSProperties;
}

function applyToDocument(style: CSSProperties, state: ThemeState) {
    const root = document.documentElement;
    for (const [name, value] of Object.entries(style)) {
        root.style.setProperty(name, String(value));
    }
    root.dataset.theme = state.theme;
    root.dataset.density = state.density;
}

interface BrandingState {
    branding: Branding | null;
    theme: ThemeState;
    rootStyle: CSSProperties;
    pageSize: number;
    reload(): Promise<void>;
    setTheme(value: string): void;
    setDensity(value: string): void;
    setSidebarMode(value: string): void;
    toggleSidebarMode(): void;
}

const BrandingContext = createContext<BrandingState | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
    const [branding, setBranding] = useState<Branding | null>(null);
    const [theme, setThemeState] = useState<ThemeState>(() => initialState(null));

    const load = useCallback(async (signal?: AbortSignal) => {
        const response = await fetch("/api/branding", {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
            signal,
        });
        if (!response.ok) {
            return;
        }
        const payload = (await response.json()) as { data: Branding };
        setBranding(payload.data);
        setThemeState(initialState(payload.data));
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        void load(controller.signal).catch(() => undefined);
        return () => controller.abort();
    }, [load]);

    const rootStyle = useMemo(() => rootStyleFor(branding), [branding]);

    useEffect(() => {
        applyToDocument(rootStyle, theme);
    }, [rootStyle, theme]);

    const save = useCallback((values: Record<string, string>) => {
        if (!branding?.user) {
            return;
        }
        void api.patch("/me/profile", values).catch(() => undefined);
    }, [branding]);

    const value = useMemo<BrandingState>(() => ({
        branding,
        theme,
        rootStyle,
        pageSize: branding && branding.page_size > 0 ? branding.page_size : 15,
        reload: () => load(),
        setTheme(next) {
            const resolved = resolveTheme(next, theme.theme);
            setThemeState((current) => ({ ...current, theme: resolved }));
            writeThemePref(resolved);
            save({ mk_theme: resolved });
        },
        setDensity(next) {
            const density = DENSITY_IDS.includes(next) ? next : "comfortable";
            setThemeState((current) => ({ ...current, density }));
            save({ mk_density: density });
        },
        setSidebarMode(next) {
            const sidebarMode = SIDEBAR_MODE_IDS.includes(next) ? next : "full";
            setThemeState((current) => ({ ...current, sidebarMode }));
            save({ mk_sidebar_mode: sidebarMode });
        },
        toggleSidebarMode() {
            const sidebarMode = theme.sidebarMode === "icon" ? "full" : "icon";
            setThemeState((current) => ({ ...current, sidebarMode }));
            save({ mk_sidebar_mode: sidebarMode });
        },
    }), [branding, theme, rootStyle, load, save]);

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingState {
    const value = useContext(BrandingContext);
    if (!value) {
        throw new Error("useBranding must be used inside BrandingProvider");
    }
    return value;
}

export function useMkTheme(): BrandingState {
    return useBranding();
}

export function useMkRoot(): { "data-mk-theme": string; "data-mk-density": string;
                                style: CSSProperties } {
    const { theme, rootStyle } = useBranding();
    return { "data-mk-theme": theme.theme, "data-mk-density": theme.density, style: rootStyle };
}
